"use client";

import { motion } from "framer-motion";
import Image from "next/image";
import { Container } from "@/components/common/Container";
import { Layout } from "@/components/layout/Layout";

export default function AboutPage() {
  return (
    <Layout title="About Swargaseema">
      <main>
        <PageHero
          eyebrow="About"
          title="A real-estate brand built on direct relationships."
          copy="Swargaseema presents its credibility through visible leadership, clear project information, and guided buyer support."
        />
        <section className="py-16 lg:py-24">
          <Container className="grid items-center gap-10 lg:grid-cols-[1fr_0.95fr]">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-[var(--gold)]">Leadership</p>
              <h2 className="font-serif text-3xl font-medium leading-tight text-[var(--ink)] sm:text-4xl lg:text-5xl">
                Founder-led trust with 20+ years of real-estate experience.
              </h2>
              <p className="mt-5 max-w-2xl text-base leading-8 text-[var(--muted)]">
                The website makes the founder visible without slowing down project comparison. Buyers can understand the brand story, then move directly into project details and site-visit enquiry.
              </p>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative flex h-[410px] items-start justify-center overflow-hidden rounded-lg bg-[var(--ink-2)] px-6 pt-8 lg:h-[520px]"
            >
              <Image
                src="/brand/founder-about-relaxed.webp"
                alt="Swargaseema founder"
                width={664}
                height={1300}
                sizes="(max-width: 800px) 82vw, 34vw"
                className="h-auto max-h-[680px] w-auto max-w-full object-contain object-top lg:max-h-[780px]"
              />
            </motion.div>
          </Container>
        </section>
      </main>
    </Layout>
  );
}

function PageHero({ eyebrow, title, copy }: { eyebrow: string; title: string; copy: string }) {
  return (
    <section className="bg-[var(--ink)] pb-20 pt-32 text-[var(--white)] lg:pt-40">
      <Container>
        <p className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-[var(--gold-strong)]">{eyebrow}</p>
        <motion.h1 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="max-w-4xl break-words font-serif text-3xl font-medium leading-tight sm:text-5xl lg:text-6xl sm:leading-none"
        >
          {title}
        </motion.h1>
        <p className="mt-5 max-w-2xl text-base leading-8 text-white/75 sm:text-lg">{copy}</p>
      </Container>
    </section>
  );
}
